import { dequeueTelemetryEvents, getTelemetryClientConfig } from '@ms/yammer-telemetry-store';
import type { DebouncedFunc } from 'lodash';
import { throttle } from 'lodash';

import { getAuthToken } from '../authentication';
import type { AnalyticsEvent, AnalyticsV2Event, ErrorEvent, InfoEvent, LogEvents, PerformanceEvent } from '../events';

import { batchReportAnalyticsEventInputs } from './batchAnalyticsEvents';
import { batchReportLogEventsInput } from './batchLogEvents';
import { getReportAnalyticsEventsInput } from './getReportAnalyticsEventsInput';
import { getReportLogEventsInput } from './getReportLogEventsInput';
import { reportDeliveryLifecycleInfo } from './reportDeliveryLifecycleInfo';
import {
  reportAnalyticsEventsNow,
  reportAnalyticsV2EventsNow,
  reportLogEventsNow,
} from './reportTelemetryEventsToServer';

type ReportTelemetryEventsInBatches = () => Promise<void>;
export const reportTelemetryEventsInBatches: ReportTelemetryEventsInBatches = async () => {
  await reportAnalyticsEventsInBatches();
  await reportLogEventsInBatches();
};

let throttledReportTelemetryEventsInBatch: DebouncedFunc<ReportTelemetryEventsInBatches> | undefined;

type ThrottledReportTelemetryEventsInBatch = (
  eventReportIntervalInMilliseconds: number
) => DebouncedFunc<ReportTelemetryEventsInBatches>;
export const getThrottledReportTelemetryEventsInBatch: ThrottledReportTelemetryEventsInBatch = (
  eventReportIntervalInMilliseconds
) => {
  throttledReportTelemetryEventsInBatch ??= throttle(
    reportTelemetryEventsInBatches,
    eventReportIntervalInMilliseconds,
    { leading: false }
  );

  return throttledReportTelemetryEventsInBatch;
};

const dequeueAnalyticsEventsFromStore = async () => {
  const token = await getAuthToken();
  if (!token) {
    reportDeliveryLifecycleInfo({
      eventInputType: 'Analytics',
      outcome: 'auth_unavailable',
      skipAuthentication: true,
    });

    return [];
  }

  return dequeueTelemetryEvents<AnalyticsEvent>('Analytics');
};

type ReportAnalyticsEventsInBatches = () => Promise<void>;
const reportAnalyticsEventsInBatches: ReportAnalyticsEventsInBatches = async () => {
  const analyticsEvents = await dequeueAnalyticsEventsFromStore();
  if (analyticsEvents.length === 0) {
    return;
  }
  const analyticsEventInputs = getReportAnalyticsEventsInput(analyticsEvents);
  const clientConfig = getTelemetryClientConfig();
  const batchSizeInBytes = clientConfig?.batchSizeInBytes ?? 80000;
  const eventBatches = batchReportAnalyticsEventInputs(analyticsEventInputs, batchSizeInBytes);

  await Promise.all(
    eventBatches.map((eventBatch) =>
      reportAnalyticsEventsNow({
        events: eventBatch,
      })
    )
  );
};

let throttledReportAnalyticsEventsInBatch: DebouncedFunc<ReportAnalyticsEventsInBatches> | undefined;

type ThrottledReportAnalyticsEventsInBatch = (
  eventReportIntervalInMilliseconds: number
) => DebouncedFunc<ReportAnalyticsEventsInBatches>;
export const getThrottledReportAnalyticsEventsInBatch: ThrottledReportAnalyticsEventsInBatch = (
  eventReportIntervalInMilliseconds
) => {
  throttledReportAnalyticsEventsInBatch ??= throttle(
    reportAnalyticsEventsInBatches,
    eventReportIntervalInMilliseconds,
    {
      leading: false,
    }
  );

  return throttledReportAnalyticsEventsInBatch;
};

const dequeueAnalyticsV2EventsFromStore = async () => {
  const token = await getAuthToken();
  if (!token) {
    reportDeliveryLifecycleInfo({
      eventInputType: 'AnalyticsV2',
      outcome: 'auth_unavailable',
      skipAuthentication: true,
    });

    return [];
  }

  return dequeueTelemetryEvents<AnalyticsV2Event>('AnalyticsV2');
};

type ReportAnalyticsV2EventsInBatches = () => Promise<void>;
const reportAnalyticsV2EventsInBatches: ReportAnalyticsV2EventsInBatches = async () => {
  const analyticsEvents = await dequeueAnalyticsV2EventsFromStore();
  if (analyticsEvents.length === 0) {
    return;
  }
  const analyticsEventInputs = analyticsEvents;
  const clientConfig = getTelemetryClientConfig();
  const batchSizeInBytes = clientConfig?.batchSizeInBytes ?? 80000;
  const eventBatches = batchReportAnalyticsEventInputs(analyticsEventInputs, batchSizeInBytes);

  await Promise.all(eventBatches.map((eventBatch) => reportAnalyticsV2EventsNow(eventBatch)));
};

let throttledReportAnalyticsV2EventsInBatch: DebouncedFunc<ReportAnalyticsEventsInBatches> | undefined;

type ThrottledReportAnalyticsV2EventsInBatch = (
  eventReportIntervalInMilliseconds: number
) => DebouncedFunc<ReportAnalyticsEventsInBatches>;
export const getThrottledReportAnalyticsV2EventsInBatch: ThrottledReportAnalyticsV2EventsInBatch = (
  eventReportIntervalInMilliseconds
) => {
  throttledReportAnalyticsV2EventsInBatch ??= throttle(
    reportAnalyticsV2EventsInBatches,
    eventReportIntervalInMilliseconds,
    {
      leading: false,
    }
  );

  return throttledReportAnalyticsV2EventsInBatch;
};

const dequeueLogEventsFromStore = () => ({
  errorEvents: dequeueTelemetryEvents<ErrorEvent>('Error'),
  infoEvents: dequeueTelemetryEvents<InfoEvent>('Info'),
  performanceEvents: dequeueTelemetryEvents<PerformanceEvent>('Performance'),
});

const hasLogEvents = ({ errorEvents, infoEvents, performanceEvents }: LogEvents) =>
  [errorEvents.length, infoEvents.length, performanceEvents.length].some((count) => count > 0);

type ReportLogEventsInBatches = () => Promise<void>;
const reportLogEventsInBatches: ReportLogEventsInBatches = async () => {
  const logEvents = dequeueLogEventsFromStore();
  if (!hasLogEvents(logEvents)) {
    return;
  }

  const reportLogEventsInput = await getReportLogEventsInput(logEvents);
  const clientConfig = getTelemetryClientConfig();
  const batchSizeInBytes = clientConfig?.batchSizeInBytes ?? 80000;
  const eventBatches = batchReportLogEventsInput(reportLogEventsInput, batchSizeInBytes);

  await Promise.all(eventBatches.map((eventBatch) => reportLogEventsNow(eventBatch)));
};

let throttledReportLogEventsInBatch: DebouncedFunc<ReportLogEventsInBatches> | undefined;

type ThrottledReportLogEventsInBatch = (
  eventReportIntervalInMilliseconds: number
) => DebouncedFunc<ReportLogEventsInBatches>;
export const getThrottledReportLogEventsInBatch: ThrottledReportLogEventsInBatch = (
  eventReportIntervalInMilliseconds
) => {
  throttledReportLogEventsInBatch ??= throttle<ReportLogEventsInBatches>(
    reportLogEventsInBatches,
    eventReportIntervalInMilliseconds,
    {
      leading: false,
    }
  );

  return throttledReportLogEventsInBatch;
};
